import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AppButton } from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import {
  platformAdminService,
  type PlatformUiDocsSyncStatus,
} from "../../../services/platformAdminService";

export default function UiDocsRagPanel() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [status, setStatus] = useState<PlatformUiDocsSyncStatus | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await platformAdminService.getUiDocsStatus());
    } catch {
      setError(t("superAdmin.uiDocs.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSync = async () => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    try {
      const next = await platformAdminService.syncUiDocs();
      setStatus(next);
      setSuccess(t("superAdmin.uiDocs.syncSuccess"));
    } catch {
      setError(t("superAdmin.uiDocs.syncError"));
      try {
        setStatus(await platformAdminService.getUiDocsStatus());
      } catch {
        /* ignore refresh failure */
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <PageSectionCard title={t("superAdmin.uiDocs.title")} description={t("superAdmin.uiDocs.description")}>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {loading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("superAdmin.uiDocs.indexLabel")}: {status?.indexName || "—"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("superAdmin.uiDocs.classpathDocs")}: {status?.classpathDocumentCount ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("superAdmin.uiDocs.indexedDocs")}: {status?.documentCount ?? 0} ·{" "}
              {t("superAdmin.uiDocs.chunks")}: {status?.chunkCount ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("superAdmin.uiDocs.lastSynced")}:{" "}
              {status?.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString() : t("superAdmin.uiDocs.never")}
            </Typography>
            {status?.syncedBy && (
              <Typography variant="body2" color="text.secondary">
                {t("superAdmin.uiDocs.syncedBy")}: {status.syncedBy}
              </Typography>
            )}
            {status?.lastError && <Alert severity="warning">{status.lastError}</Alert>}
            {!status?.ready && !status?.lastError && (
              <Alert severity="info">{t("superAdmin.uiDocs.notReady")}</Alert>
            )}
            <Stack direction="row" spacing={1.5}>
              <AppButton variant="contained" onClick={onSync} loading={syncing} disabled={syncing}>
                {t("superAdmin.uiDocs.syncNow")}
              </AppButton>
              <AppButton variant="outlined" onClick={() => void load()} disabled={loading || syncing}>
                {t("superAdmin.uiDocs.refresh")}
              </AppButton>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("superAdmin.uiDocs.hint")}
            </Typography>
          </>
        )}
      </Stack>
    </PageSectionCard>
  );
}
